import { Price } from "../../api-clients/price-api"
import {
  ProductDefinition,
  ProductDefinitionArticlesArticlesList,
  ProductDefinitionArticlesAttributeValues,
  ProductDefinitionArticlesCadDrawingsList,
  ProductDefinitionArticlesGallery,
  SimpleAssetListModel,
} from "../../api-clients/product-api-v1"
import {
  ATTRIBUTE_TYPE_COMMERCIAL,
  ATTRIBUTE_TYPE_ESSENTIAL,
  ATTRIBUTE_TYPE_TECHNICAL,
  attributeIDs,
} from "../../components/features/article-table/common/constants"
import { zipFileDownloadOptions } from "../constants/constants"
import {
  ArticleTableArticleAccessoryType,
  ArticleTableArticleAttributeValueType,
  ArticleTableArticleType,
  ArticleTableAttributeType,
  ArticleTableDataType,
} from "../types/articleTable"
import { ZipFileDownloadOptionType } from "../types/global"
import { PriceConfigType } from "../types/prices"
import { AvailableZipOptionsType } from "../types/product"
import { sortImages } from "./pdp-utils"

export const getArticleTableData = (product: ProductDefinition): ArticleTableDataType => {
  const { articles } = product
  const articleTableData: ArticleTableDataType = {
    articles: {
      articlesList: getArticlesList(product, [attributeIDs.SYSTEMS]),
      articlesListSize: articles.articlesListSize,
      attributesList: getAttributesList(product, [attributeIDs.SYSTEMS]),
    },
  }

  return articleTableData
}

export const getArchivedArticleTableData = (product: ProductDefinition): ArticleTableDataType => {
  const { articles } = product
  const articlesList = getArticlesList(product)
  const allAttributes = getAttributesList(product)
  //filter out attributes without values
  const attributesList = allAttributes.filter(({ attributeId }) => {
    return articlesList.some(({ attributeValues }) => {
      return attributeValues.some(({ attributeId: attrId }) => attrId === attributeId)
    })
  })
  const articleTableData: ArticleTableDataType = {
    articles: {
      articlesList,
      articlesListSize: articles.articlesListSize,
      attributesList,
    },
  }

  return articleTableData
}

const getArticlesList = (product: ProductDefinition, attributesToOmit?: string[]) => {
  const { articles } = product
  const normalizedArticlesList = articles.articlesList.reduce((list, article) => {
    const { state, key: articleCode, tenderTextProductId, gallery, attributeValues, accessories } = article
    const availableZipOptions = getAvailableZipOptions(null, article)

    list.push({
      state,
      attributeValues: getAttributeValues(attributeValues, attributesToOmit),
      articleCode,
      mainImageUrl: getMainImageUrl(gallery),
      systems: getSystemsForArticle(attributeValues),
      tenderTextId: tenderTextProductId ?? null,
      zipCadDrawingsLink: availableZipOptions.cadDrawings ? articleCode : null,
      zipPhotosDrawingsLink: articleCode, // Always present for article table
      availableZipOptionsFormData: availableZipOptions,
      accessories: getAccessories(accessories),
    })

    return list
  }, [] as Array<ArticleTableArticleType>)

  return normalizedArticlesList
}

const getAttributesList = (product: ProductDefinition, attributesToOmit?: string[]) => {
  const { articles } = product

  const normalizedAttributesList = articles.attributesList.reduce((list, attribute) => {
    const { key: attributeId, name } = attribute

    if (attributesToOmit?.includes(attributeId)) return list

    list.push({
      attributeId,
      name,
      type: getAttributeType(attributeId),
    })

    return list
  }, [] as Array<ArticleTableAttributeType>)

  return normalizedAttributesList
}

const getAttributeType = (attributeId: string) => {
  if (attributeIDs.ESSENTIAL.has(attributeId)) return ATTRIBUTE_TYPE_ESSENTIAL
  if (attributeIDs.COMMERCIAL.has(attributeId)) return ATTRIBUTE_TYPE_COMMERCIAL
  return ATTRIBUTE_TYPE_TECHNICAL
}

const getAttributeValues = (
  attributeValues: ProductDefinitionArticlesAttributeValues[],
  attributesToOmit?: string[]
) => {
  const normalizedAttributeValues = attributeValues.reduce((list, attributeValue) => {
    const { key: attributeId, value, rawValue, unit } = attributeValue

    // If "systems" attribute ommit it from the table
    if (attributesToOmit?.includes(attributeId)) return list

    list.push({
      attributeId,
      value,
      rawValue,
      unit,
    })

    return list
  }, [] as Array<ArticleTableArticleAttributeValueType>)

  return normalizedAttributeValues
}

const getSystemsForArticle = (attributeValues: ProductDefinitionArticlesAttributeValues[]) => {
  const systemsAttribute = attributeValues.find((attr) => attr.key === attributeIDs.SYSTEMS)
  /* If we have a systems attribute in the attributes list, get all the system values which are comma separated
    then trim the values inside because we have no guarantees if the come split by "," or by ", " */
  const systems = systemsAttribute ? systemsAttribute.rawValue?.split(",").map((s) => s.trim()) : null

  return systems
}

const getMainImageUrl = (gallery: ProductDefinitionArticlesGallery) => {
  const { list: articleImages } = gallery
  return articleImages?.length ? sortImages(articleImages, true)?.[0]?.thumbnail : null
}

export const getAvailableZipOptions = (product: ProductDefinition, article: ProductDefinitionArticlesArticlesList) => {
  /* On article level get available file types from the article model, on product level gather all files from all articles */
  const drawingsFiles = article
    ? article?.cadDrawings?.list
    : product?.articles?.articlesList?.reduce((files: Array<ProductDefinitionArticlesCadDrawingsList>, article) => {
        return [...files, ...article.cadDrawings?.list]
      }, [])

  const fileTypes = new Set(drawingsFiles?.map((f) => getDocumentContentType(f.contentType)))

  const availableZipOptions: AvailableZipOptionsType = {
    cadDrawings: !!fileTypes.size,
    dxf: fileTypes.has(zipFileDownloadOptions.DXF),
    dwg: fileTypes.has(zipFileDownloadOptions.DWG),
    dxf_3D: fileTypes.has(zipFileDownloadOptions.DXF_3D),
    dwg_3D: fileTypes.has(zipFileDownloadOptions.DWG_3D),
    iges_3D: fileTypes.has(zipFileDownloadOptions.IGES_3D),
    obj: fileTypes.has(zipFileDownloadOptions.OBJ),
    step_3D: fileTypes.has(zipFileDownloadOptions.STEP_3D),
    sat_3D: fileTypes.has(zipFileDownloadOptions.SAT_3D),
    csv: product ? true : false, // Always true on product level
    datasheet: product ? true : false, // Always true on product level
    documents: article ? false : !!product?.documents?.list?.length, // Always false for article table
    overviewtables: article
      ? false
      : !!product?.accessoriesOverviews?.list?.length || !!product?.intendedUseOverviews?.list?.length, // Always false for article table
    photos: product ? true : false, // Always true on product level
    tendertext: article ? false : product?.articles?.articlesList?.some((article) => !!article.tenderTextProductId), // Always false for article table
  }

  return availableZipOptions
}

const getDocumentContentType = (contentType: string) => {
  return contentType.includes("3D") ? contentType.split("3").join("_3") : contentType
}

/* Example for valid selectedOptions [ "CSV", "DATA_SHEET", "PHOTOS_DRAWINGS", "CAD_DRAWINGS[DXF,DWG,DXF_3D,DWG_3D,IGES_3D,STEP_3D,SAT_3D,OBJ]" ]
    CAD_DRAWINGS is passed as string but it has array with options inside of it */
export const getSelectedOptionsAsStrings = (
  availableZipOptions: Partial<AvailableZipOptionsType>,
  locale: string
): Array<ZipFileDownloadOptionType | string> => {
  const selectedOptions = []

  if (availableZipOptions.csv) selectedOptions.push(zipFileDownloadOptions.CSV)
  if (availableZipOptions.datasheet) selectedOptions.push(zipFileDownloadOptions.DATA_SHEET)
  if (availableZipOptions.documents) selectedOptions.push(zipFileDownloadOptions.DOCUMENTS)
  if (availableZipOptions.overviewtables) selectedOptions.push(zipFileDownloadOptions.OVERVIEW_TABLES)
  if (availableZipOptions.photos) selectedOptions.push(zipFileDownloadOptions.PHOTOS_DRAWINGS)
  if (availableZipOptions.tendertext && locale === "de-DE") selectedOptions.push(zipFileDownloadOptions.TENDER_TEXTS)

  if (availableZipOptions.cadDrawings) {
    const cadDrawingsOptions = []

    if (availableZipOptions.dxf) cadDrawingsOptions.push(zipFileDownloadOptions.DXF)
    if (availableZipOptions.dwg) cadDrawingsOptions.push(zipFileDownloadOptions.DWG)
    if (availableZipOptions.dxf_3D) cadDrawingsOptions.push(zipFileDownloadOptions.DXF_3D)
    if (availableZipOptions.dwg_3D) cadDrawingsOptions.push(zipFileDownloadOptions.DWG_3D)
    if (availableZipOptions.iges_3D) cadDrawingsOptions.push(zipFileDownloadOptions.IGES_3D)
    if (availableZipOptions.step_3D) cadDrawingsOptions.push(zipFileDownloadOptions.STEP_3D)
    if (availableZipOptions.sat_3D) cadDrawingsOptions.push(zipFileDownloadOptions.SAT_3D)
    if (availableZipOptions.obj) cadDrawingsOptions.push(zipFileDownloadOptions.OBJ)

    const cadDrawingsOptionsString = zipFileDownloadOptions.CAD_DRAWINGS + "[" + cadDrawingsOptions.join(",") + "]"

    selectedOptions.push(cadDrawingsOptionsString)
  }

  return selectedOptions
}

export const hasCadDrawings = (zipOptions: Partial<AvailableZipOptionsType>): boolean => {
  const { dxf, dwg, dxf_3D, dwg_3D, iges_3D, step_3D, sat_3D, obj } = zipOptions

  return !!(dxf || dwg || dxf_3D || dwg_3D || iges_3D || step_3D || sat_3D || obj)
}

export const isCadDrawingOption = (option: string): boolean => {
  const { DXF, DWG, DXF_3D, DWG_3D, IGES_3D, STEP_3D, SAT_3D, OBJ, CAD_DRAWINGS } = zipFileDownloadOptions

  const ucOption = option.toUpperCase()

  return (
    ucOption === DXF ||
    ucOption === DWG ||
    ucOption === DXF_3D ||
    ucOption === DWG_3D ||
    ucOption === IGES_3D ||
    ucOption === STEP_3D ||
    ucOption === SAT_3D ||
    ucOption === OBJ ||
    ucOption === CAD_DRAWINGS.split("_").join("")
  )
}

export const getDefaultSelectedOptions = (
  availableZipOptions: AvailableZipOptionsType,
  userPreselectedOptions: Partial<AvailableZipOptionsType>
) => {
  return Object.keys(availableZipOptions)
    .filter((opt) => availableZipOptions[opt])
    .reduce((selected: Partial<AvailableZipOptionsType>, option: string) => {
      // If user is logged in, get the pre-selected options from the user settings
      if (userPreselectedOptions) {
        if (option === "cadDrawings") {
          selected[option] = hasCadDrawings(userPreselectedOptions)
          return selected
        }
        selected[option] = userPreselectedOptions[option] ?? true
      }
      // If user is not logged in, set cad drawings options to false and all else to pre-selected
      else {
        selected[option] = isCadDrawingOption(option) ? false : true
      }

      return selected
    }, {})
}

export const addPricesToArticleTable = (
  articleTableData: ArticleTableDataType,
  priceData: Price,
  priceConfig: PriceConfigType,
  priceLabels: string[]
): ArticleTableDataType => {
  // If there are no prices provided in the data, just return the same article talbe data
  if (!priceData?.length) return articleTableData

  const { articles } = articleTableData
  const { articlesList, attributesList } = articles

  const enrichedArticles = getArticlesWithPriceAttributeValues(articlesList, priceData, priceConfig, priceLabels)
  const enrichedAttributes = attributesList.concat(getPriceAttributes(priceConfig, priceLabels))

  return {
    ...articleTableData,
    articles: { ...articles, articlesList: enrichedArticles, attributesList: enrichedAttributes },
  }
}

const getPriceAttributes = (priceConfig: PriceConfigType, priceLabels: string[]) => {
  const priceAttributes = []

  // If net price is configured, add it to the attributes list
  if (priceConfig.hasNetPrice) {
    priceAttributes.push({
      attributeId: attributeIDs.NET_PRICE,
      name: priceLabels[0],
      type: ATTRIBUTE_TYPE_COMMERCIAL,
    })
  }

  // If gross price is configured, add it to the attributes list
  if (priceConfig.hasGrossPrice) {
    priceAttributes.push({
      attributeId: attributeIDs.GROSS_PRICE,
      name: priceLabels[2],
      type: ATTRIBUTE_TYPE_COMMERCIAL,
    })
  }

  return priceAttributes
}

const getArticlesWithPriceAttributeValues = (
  articles: ArticleTableArticleType[],
  priceData: Price,
  priceConfig: PriceConfigType,
  priceLabels: string[]
) => {
  return articles.map((article) => {
    const articlePriceData = priceData.find((pd) => article.articleCode === pd.articleNumber)

    if (articlePriceData) {
      if (priceConfig.hasNetPrice) {
        article.attributeValues.push({
          attributeId: attributeIDs.NET_PRICE,
          value: formatPrice(articlePriceData.netPrice, priceConfig),
          rawValue: formatPrice(articlePriceData.netPrice, priceConfig),
          unit: [
            {
              encoding: "geberit",
              value: priceLabels[1],
            },
          ],
        })
      }

      if (priceConfig.hasGrossPrice) {
        article.attributeValues.push({
          attributeId: attributeIDs.GROSS_PRICE,
          value: formatPrice(articlePriceData.grossPrice, priceConfig),
          rawValue: formatPrice(articlePriceData.grossPrice, priceConfig),
          unit: [
            {
              encoding: "geberit",
              value: priceLabels[3],
            },
          ],
        })
      }
    }

    return article
  })
}

// Return a price formatted using the getFormatOptions function
const formatPrice = (price: string, priceConfig: PriceConfigType) => {
  if (!price) return ""
  // Get the format options from the Prismic config
  const { rounding, decimalPoint, thousandSeparator } = priceConfig

  const roundedPrice = Number(price).toFixed(rounding)
  const [thousandDigits, fractionDigits] = roundedPrice.split(".")

  // Get the whole number part of the price formatted with the thousandSymbol
  const formattedThousands = formatThousands(thousandDigits, thousandSeparator)

  const formattedFraction = fractionDigits ?? ""

  return formattedFraction ? `${formattedThousands}${decimalPoint}${formattedFraction}` : formattedThousands
}

// Inserts the separator symbol in the whole part of the number
const formatThousands = (value: string, separator: string) =>
  value.toString().replace(/\B(?=([0-9]{3})+(?![0-9]))/g, separator)

// Extracts the accessories product codes for each article
const getAccessories = (accessories: SimpleAssetListModel): ArticleTableArticleAccessoryType[] | null => {
  return !!accessories?.list?.length
    ? accessories.list.map((acc) => {
        return { productId: acc.key, articleCode: acc.sku }
      })
    : null
}
